import { Fragment, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import {
  Search,
  Loader2,
  Filter,
  RefreshCw,
  Activity,
  ShieldAlert,
  UserCog,
  Shield,
} from 'lucide-react'
import { apiGet } from '../lib/api'

interface ActivityItem {
  id: string
  actorId: string | null
  actorEmail: string | null
  actorRole: string | null
  action: string
  entityType: string | null
  entityId: string | null
  summary: string | null
  meta: unknown
  ip: string | null
  userAgent: string | null
  createdAt: string | null
}

interface ActivityData {
  items: ActivityItem[]
  total: number
  page: number
  perPage: number
  pages: number
  actions: string[]
  actors: { id: string | null; email: string | null; role: string | null }[]
}

function fmtDate(d: string | null): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return '—'
  }
}

function roleBadge(role: string | null) {
  if (role === 'admin') {
    return (
      <Badge variant="danger">
        <Shield className="size-3" /> Admin
      </Badge>
    )
  }
  if (role === 'operator') {
    return (
      <Badge variant="info">
        <UserCog className="size-3" /> Operador
      </Badge>
    )
  }
  return <Badge variant="default">{role || 'sistema'}</Badge>
}

function actionBadge(action: string) {
  const prefix = action.split('.')[0]
  // Tonos suaves por prefijo
  let cls = 'bg-ink-100 text-ink-700'
  if (prefix === 'order') cls = 'bg-blue-100 text-blue-700'
  else if (prefix === 'product') cls = 'bg-amber-100 text-amber-700'
  else if (prefix === 'auth') cls = 'bg-emerald-100 text-emerald-700'
  else if (prefix === 'refund') cls = 'bg-rose-100 text-rose-700'
  else if (prefix === 'payment_method') cls = 'bg-violet-100 text-violet-700'
  else if (prefix === 'user') cls = 'bg-cyan-100 text-cyan-700'
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 font-mono text-[11px] font-medium ${cls}`}>
      {action}
    </span>
  )
}

function initials(emailOrName: string | null): string {
  if (!emailOrName) return '?'
  const base = emailOrName.split('@')[0] || emailOrName
  const parts = base.split(/[._\-\s]+/).filter(Boolean)
  if (parts.length === 0) return base.slice(0, 2).toUpperCase()
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function prettyMeta(meta: unknown): string {
  if (meta == null) return ''
  if (typeof meta === 'string') {
    try {
      return JSON.stringify(JSON.parse(meta), null, 2)
    } catch {
      return meta
    }
  }
  try {
    return JSON.stringify(meta, null, 2)
  } catch {
    return String(meta)
  }
}

export function AuditLog() {
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [qApplied, setQApplied] = useState('')
  const [actorFilter, setActorFilter] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [entityTypeFilter, setEntityTypeFilter] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(page))
    p.set('limit', '20')
    if (qApplied) p.set('q', qApplied)
    if (actorFilter) p.set('actor', actorFilter)
    if (actionFilter) p.set('action', actionFilter)
    if (entityTypeFilter) p.set('entity_type', entityTypeFilter)
    if (from) p.set('from', new Date(from).toISOString())
    if (to) p.set('to', new Date(to).toISOString())
    return p
  }, [page, qApplied, actorFilter, actionFilter, entityTypeFilter, from, to])

  const { data, isLoading, isFetching, refetch } = useQuery<ActivityData>({
    queryKey: ['audit-log', params.toString()],
    queryFn: () => apiGet<ActivityData>(`/api/admin/audit-log?${params.toString()}`),
    placeholderData: (prev) => prev,
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setQApplied(q.trim())
  }

  function resetFilters() {
    setQ('')
    setQApplied('')
    setActorFilter('')
    setActionFilter('')
    setEntityTypeFilter('')
    setFrom('')
    setTo('')
    setPage(1)
  }

  // Derivar lista de entity types únicos a partir de los actions disponibles
  const entityTypes = useMemo(() => {
    const set = new Set<string>()
    for (const a of data?.actions || []) {
      const t = a.split('.')[0]
      if (t) set.add(t)
    }
    return Array.from(set).sort()
  }, [data?.actions])

  return (
    <main className="min-h-screen bg-ink-50">
      <Topbar
        title="Audit log"
        subtitle={
          data
            ? `${data.total.toLocaleString('es-MX')} acciones registradas`
            : 'Cargando…'
        }
        actions={
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <RefreshCw className={`size-3.5 ${isFetching ? 'animate-spin' : ''}`} /> Refrescar
          </button>
        }
      />
      <div className="p-6">
        <form
          onSubmit={onSearch}
          className="mb-4 grid gap-2 rounded-2xl border border-ink-200 bg-white p-3 md:grid-cols-12"
        >
          <div className="relative md:col-span-4">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar en summary, email, acción…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>

          <select
            value={actorFilter}
            onChange={(e) => {
              setActorFilter(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 md:col-span-3"
            title="Filtrar por actor"
          >
            <option value="">Todos los actores</option>
            {(data?.actors || []).map((a) => (
              <option key={a.id || a.email || 'unk'} value={a.id || a.email || ''}>
                {a.email || `Usuario #${a.id}`} ({a.role || 's/r'})
              </option>
            ))}
          </select>

          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 md:col-span-3"
            title="Filtrar por acción"
          >
            <option value="">Todas las acciones</option>
            {(data?.actions || []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>

          <select
            value={entityTypeFilter}
            onChange={(e) => {
              setEntityTypeFilter(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 md:col-span-2"
            title="Tipo de entidad"
          >
            <option value="">Todas las entidades</option>
            {entityTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <input
            type="datetime-local"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 md:col-span-3"
            title="Desde"
          />
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => {
              setTo(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 md:col-span-3"
            title="Hasta"
          />

          <div className="flex gap-2 md:col-span-6">
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Filter className="size-3.5" /> Aplicar filtros
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              Limpiar
            </button>
          </div>
        </form>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando actividad…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Fecha</th>
                      <th className="px-4 py-3 font-medium">Actor</th>
                      <th className="px-4 py-3 font-medium">Acción</th>
                      <th className="px-4 py-3 font-medium">Entidad</th>
                      <th className="px-4 py-3 font-medium">Resumen</th>
                      <th className="px-4 py-3 font-medium">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-sm text-ink-500">
                          <div className="flex flex-col items-center gap-2">
                            <ShieldAlert className="size-8 text-ink-300" />
                            <span>Sin actividad registrada para los filtros aplicados</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {data.items.map((r) => (
                      <Fragment key={r.id}>
                        <tr
                          className="cursor-pointer border-t border-ink-100 hover:bg-ink-50"
                          onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                        >
                          <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-ink-700">
                            {fmtDate(r.createdAt)}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <div className="grid size-7 place-items-center rounded-full bg-brand-100 text-[10px] font-bold uppercase text-brand-700">
                                {initials(r.actorEmail)}
                              </div>
                              <div className="flex flex-col">
                                <span className="text-xs font-medium text-ink-900">
                                  {r.actorEmail || (r.actorId ? `Usuario #${r.actorId}` : 'sistema')}
                                </span>
                                <span>{roleBadge(r.actorRole)}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2">{actionBadge(r.action)}</td>
                          <td className="px-4 py-2 text-xs text-ink-700">
                            {r.entityType ? (
                              <span>
                                {r.entityType}
                                {r.entityId && (
                                  <span className="text-ink-400"> #{r.entityId}</span>
                                )}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="max-w-md truncate px-4 py-2 text-xs text-ink-700">
                            {r.summary || '—'}
                          </td>
                          <td className="px-4 py-2 font-mono text-[11px] text-ink-500">
                            {r.ip || '—'}
                          </td>
                        </tr>
                        {expanded === r.id && (
                          <tr className="border-t border-ink-100 bg-ink-50/50">
                            <td colSpan={6} className="px-4 py-3">
                              <div className="space-y-3 text-xs">
                                <div className="flex flex-wrap gap-x-6 gap-y-1">
                                  <div>
                                    <span className="font-semibold text-ink-700">ID:</span>{' '}
                                    <span className="font-mono text-ink-900">{r.id}</span>
                                  </div>
                                  {r.userAgent && (
                                    <div className="max-w-2xl truncate">
                                      <span className="font-semibold text-ink-700">User-Agent:</span>{' '}
                                      <span className="font-mono text-ink-600">{r.userAgent}</span>
                                    </div>
                                  )}
                                </div>
                                {r.summary && (
                                  <div>
                                    <span className="font-semibold text-ink-700">Resumen:</span>{' '}
                                    <span className="text-ink-900">{r.summary}</span>
                                  </div>
                                )}
                                {r.meta != null && (
                                  <div>
                                    <div className="mb-1 flex items-center gap-1.5 font-semibold text-ink-700">
                                      <Activity className="size-3.5" /> Meta (before/after):
                                    </div>
                                    <pre className="max-h-96 overflow-auto rounded-md bg-ink-900 px-3 py-2 font-mono text-[11px] leading-relaxed text-ink-100">
                                      {prettyMeta(r.meta)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong> (
                  {data.total.toLocaleString('es-MX')} acciones)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  )
}

export default AuditLog
